import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import { Dashboard, NLQQueryResult, WidgetConfig, SavedReport } from '../queries/dashboard.queries';

// ================================
// NLQ MUTATIONS
// ================================

export interface NLQQueryInput {
  query: string;
  branchId?: string;
  sessionId?: string;
}

/**
 * Execute a natural language query
 */
export function useNLQQuery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: NLQQueryInput) => {
      const response = await apiClient.post<APIResponse<NLQQueryResult>>(
        '/dashboard/nlq',
        input
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'nlq', 'history'] });
    },
  });
}

// ================================
// DASHBOARD MUTATIONS
// ================================

export interface CreateDashboardInput {
  name: string;
  description?: string;
  type?: 'personal' | 'team' | 'system';
  category?: 'overview' | 'sales' | 'inventory' | 'operations' | 'loyalty' | 'custom';
  widgets?: WidgetConfig[];
  layout?: {
    columns?: number;
    rowHeight?: number;
    gap?: number;
  };
  defaultFilters?: {
    dateRange?: {
      type: 'relative' | 'absolute';
      value: string;
    };
    branchId?: string;
  };
  isPublic?: boolean;
}

export interface UpdateDashboardInput {
  name?: string;
  description?: string;
  widgets?: WidgetConfig[];
  layout?: {
    columns?: number;
    rowHeight?: number;
    gap?: number;
  };
  defaultFilters?: Record<string, unknown>;
  isPublic?: boolean;
  isPinned?: boolean;
}

/**
 * Create new dashboard
 */
export function useCreateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDashboardInput) => {
      const response = await apiClient.post<APIResponse<{ dashboard: Dashboard }>>(
        '/dashboard',
        input
      );
      return response.data.data.dashboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'list'] });
    },
  });
}

/**
 * Update dashboard
 */
export function useUpdateDashboard(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateDashboardInput) => {
      const response = await apiClient.patch<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${slug}`,
        input
      );
      return response.data.data.dashboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', slug] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'list'] });
    },
  });
}

/**
 * Delete dashboard
 */
export function useDeleteDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await apiClient.delete<APIResponse<{ message: string }>>(
        `/dashboard/${slug}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'list'] });
    },
  });
}

/**
 * Pin/unpin dashboard
 */
export function usePinDashboard(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (isPinned: boolean) => {
      const response = await apiClient.patch<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${slug}`,
        { isPinned }
      );
      return response.data.data.dashboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', slug] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'list'] });
    },
  });
}

// ================================
// WIDGET MUTATIONS
// ================================

export interface AddWidgetInput {
  dashboardSlug: string;
  widget: Omit<WidgetConfig, 'id' | 'createdAt' | 'updatedAt'>;
}

export interface UpdateWidgetInput {
  dashboardSlug: string;
  widgetId: string;
  updates: Partial<WidgetConfig>;
}

export interface RemoveWidgetInput {
  dashboardSlug: string;
  widgetId: string;
}

/**
 * Add widget to dashboard
 */
export function useAddWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AddWidgetInput) => {
      // First get current dashboard
      const dashboardResponse = await apiClient.get<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${input.dashboardSlug}`
      );
      const dashboard = dashboardResponse.data.data.dashboard;

      // Add new widget with generated ID
      const newWidget: WidgetConfig = {
        ...input.widget,
        id: `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      } as WidgetConfig;

      const updatedWidgets = [...dashboard.widgets, newWidget];

      // Update dashboard
      const response = await apiClient.patch<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${input.dashboardSlug}`,
        { widgets: updatedWidgets }
      );
      return response.data.data.dashboard;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', variables.dashboardSlug] });
    },
  });
}

/**
 * Update widget in dashboard
 */
export function useUpdateWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateWidgetInput) => {
      // First get current dashboard
      const dashboardResponse = await apiClient.get<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${input.dashboardSlug}`
      );
      const dashboard = dashboardResponse.data.data.dashboard;

      // Update widget
      const updatedWidgets = dashboard.widgets.map((w) =>
        w.id === input.widgetId ? { ...w, ...input.updates } : w
      );

      // Update dashboard
      const response = await apiClient.patch<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${input.dashboardSlug}`,
        { widgets: updatedWidgets }
      );
      return response.data.data.dashboard;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', variables.dashboardSlug] });
    },
  });
}

/**
 * Remove widget from dashboard
 */
export function useRemoveWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RemoveWidgetInput) => {
      // First get current dashboard
      const dashboardResponse = await apiClient.get<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${input.dashboardSlug}`
      );
      const dashboard = dashboardResponse.data.data.dashboard;

      // Remove widget
      const updatedWidgets = dashboard.widgets.filter((w) => w.id !== input.widgetId);

      // Update dashboard
      const response = await apiClient.patch<APIResponse<{ dashboard: Dashboard }>>(
        `/dashboard/${input.dashboardSlug}`,
        { widgets: updatedWidgets }
      );
      return response.data.data.dashboard;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', variables.dashboardSlug] });
    },
  });
}

// ================================
// SAVED REPORTS MUTATIONS
// ================================

export interface CreateReportInput {
  name: string;
  description?: string;
  type: 'sales' | 'inventory' | 'operations' | 'loyalty' | 'waste' | 'custom';
  dataSource: string;
  filters?: Record<string, unknown>;
  groupBy?: string;
  aggregations?: Array<{
    field: string;
    operation: 'sum' | 'avg' | 'count' | 'min' | 'max';
  }>;
  isScheduled?: boolean;
  schedule?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    dayOfWeek?: number;
    dayOfMonth?: number;
    time: string;
    timezone?: string;
  };
  recipients?: Array<{
    type: 'email' | 'slack' | 'webhook';
    address: string;
    format?: 'pdf' | 'excel' | 'csv';
  }>;
  format?: {
    type: 'table' | 'chart' | 'summary';
    chartType?: string;
    columns?: string[];
  };
}

/**
 * Create saved report
 */
export function useCreateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateReportInput) => {
      const response = await apiClient.post<APIResponse<{ report: SavedReport }>>(
        '/dashboard/reports',
        input
      );
      return response.data.data.report;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'reports'] });
    },
  });
}

/**
 * Delete saved report
 */
export function useDeleteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportId: string) => {
      const response = await apiClient.delete<APIResponse<{ message: string }>>(
        `/dashboard/reports/${reportId}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'reports'] });
    },
  });
}

// ================================
// SYSTEM DASHBOARD MUTATIONS
// ================================

/**
 * Seed system dashboards (admin only)
 */
export function useSeedSystemDashboards() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<
        APIResponse<{ message: string; dashboards: string[] }>
      >('/dashboard/system/seed');
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'list'] });
    },
  });
}
